<script setup>
import { NFlex } from 'naive-ui'; 
import { computed } from 'vue';
import GradeClassChart from '@/components/result/ai-literacy-2026/GradeClassChart.vue'; 
import { classAchievementPhrase } from './data/reportLogic';

const props = defineProps({ 
  tableData: { 
    type: Array,
    required: true,
    default: () => []
  },
  achievementMessage: {
    type: Object,
    default: () => ({
      highLevelRate: '85.2',
      lowLevelRate: '14.8'
    })
  }
});

// tableData를 차트용 데이터 포맷으로 변환
const currentClassData = computed(() => {
  // 전체 학생 수 계산
  const totalStudents = props.tableData.reduce((acc, row) => {
    const count = row.students ? row.students.split(',').filter(n => n.trim()).length : 0;
    return acc + count;
  }, 0);

  // 테이블 데이터 내림차순
  return props.tableData.map(row => {
    const count = row.students ? row.students.split(',').filter(n => n.trim()).length : 0;
    const percentage = totalStudents > 0 ? (count / totalStudents * 100).toFixed(1) : 0;
    
    return {
      level: `${row.level}수준`, 
      percentage: percentage,
      count: count
    };
  });
});

// 성취 분석 메시지 생성
const achievementResultMessage = computed(() => {
  const highRate = props.achievementMessage.highLevelRate;
  const lowRate = props.achievementMessage.lowLevelRate;

  const text = classAchievementPhrase(highRate); // 70 이상 우수한 / 40 이상 양호한 / 그 외 미흡한

  
  return `전체 학생의 ${highRate}%가 3수준 이상에 도달하여 전반적으로 ${text} 성취 분포를 보이고 있습니다.<br>그러나 ${lowRate}%의 학생은 추가적인 교육적 지원이 필요한 것으로 나타났습니다.`;
});
</script>

<template>
  <div class="card rate mt-2">
    <h5 class="card-title">성취 수준별 학생 비율(수)</h5>
    <p class="msg-info py-2">다음은 AI·디지털 리터러시에 대한 학급 학생들의 성취 수준 분포를 나타냅니다.</p>
    <n-flex :wrap="false" :size="16">      
      <GradeClassChart
          :level-data="currentClassData"
          :max-scale="100"
          chart-height="290px"
      />
      <div class="ai-table primary">
        <div class="row thead">
          <div class="th">수준(점수)</div>
          <div class="td">명단</div>
        </div>
        <div class="row" v-for="(row, index) in props.tableData" :key="index">
          <div class="th"><span class="step">{{ row.level }}수준</span>({{ row.scoreRange }})</div>
          <div class="td">{{ row.students || '' }}</div>
        </div>
      </div>
               
    </n-flex>    
    <p class="msg-result pt-2" v-html="achievementResultMessage"></p>
  </div>
</template>

<style lang="scss" scoped>
@use "sass:list";
$ai-levels: (
  var(--result-ai-primary-90),
  var(--result-ai-primary-80),  
  var(--result-ai-primary-70),
  var(--result-ai-primary-60), 
);
// 막대그래프와 명단 표를 5:5로 나눈다 (표가 넓어 막대 간격이 좁던 것을 조정).
// flex-basis를 0으로 두면 차트의 안쪽 여백만큼 차트가 넓어져 5:5가 되지 않는다.
:deep(.level-chart-wrapper) {
  flex: 1 1 calc(50% - 8px);
  min-width: 0;
}
.ai-table {
  flex: 1 1 calc(50% - 8px);
  min-width: 0;
  max-width: none !important;
  .row {
    > div {       
      &.th { min-width: auto !important; width: 130px; }     
    }  
    &.thead .td { display: flex; align-items: center; justify-content: center; } 
    &:not(.thead) {
       > div {
        padding: 1rem 1rem ;
        &.th {
          flex-direction: column;
          font-size: 1.125rem;
          color: var(--result-ai-primary-800);
          font-weight: 500;
          .step {
            font-weight: 600;
            border-radius: 2.25rem;
            padding: .25rem 1rem;
            line-height: 1.6;
            font-size: 1.25rem;
            color: var(--result-ai-blak);
            margin-bottom: .25rem;
          }
        }
        &.td { display: flex; align-items: center; font-size: 1.1rem; line-height: 1.6; }
      }
    }
  }
   @for $i from 2 through 5 {
    .row {
      &:nth-child(#{$i}) {
        .step {
          background-color: list.nth($ai-levels, $i - 1);        
        }
      }
    }    
  }  
}
.msg-result {
  font-size: 1rem;
  font-weight: 600;
  position: relative;
  padding-left: 31px;
  &::before {
    content: '';
    position: absolute;    
    left: 0;
    margin-top: -1px;
    width: 23px;
    height: 23px;
    background-image: url("@/assets/images/result/ai/icon_right.png");
  }
}
</style>